"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import type { EditedRow, EditorConfigItem, EditorTableProps, TableData, UpdateRowData } from "@/src/types/table-data.types";
import Table,{ TableRef } from "./Table";
import NavigationButtons from '../Button.Navigation'
import "./table.css"
import Editor from "./Editor";
import { passLoggedResponse } from "@/src/utils/router";
import { useRouter } from "next/navigation";
import type { LoggedResponse } from "@/src/types/request-response.types";
import { getKeys } from "@/src/utils/table-data";

const EMPTY_DATA = { body: [], config: { head: [], primaryKey: [], pKIdx: [] } };
const PAGE_SIZE = 10;
const INITIAL_PAGE_SIZE = 40;
const MAX_ROOT_ROWS = 40;

function mergeUniqueRootRows(rows: TableData["body"], config: TableData["config"]): TableData["body"] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const keys = getKeys(config, { level: 0, index: 0 }, row);
    if (keys.length === 0) return true;
    const identity = keys.map(({ key, value }) => `${key}=${value ?? ""}`).join("\u001f");
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
}
// const EMPTY_SAVED_DATA:UpdateRowData = {master:[],detail:[]}

export default function EditorTable({
  title = "Transaction Editor",
  apiCall = {
    listTable: async () => (
      { body: [], config: { head: [], primaryKey: [], pKIdx: [] } }
    )
  },
}: EditorTableProps) {
  
  const tableRef = useRef<TableRef | null>(null);
  const selectedRowIndexRef = useRef(-1);
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
  const [isChanged, setChanged] = useState(false);
  const [editedRow, setEditedRow] = useState<EditedRow>({ rowIndex: -1, row: [] });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<TableData>(EMPTY_DATA);
  const [config, setConfig] = useState<EditorConfigItem[]>([]);
  // const [savedData, setSavedData] = useState<UpdateRowData>(EMPTY_SAVED_DATA)
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [dataVersion, setDataVersion] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const windowStartRef = useRef(0);
  const nextOffsetRef = useRef(PAGE_SIZE);

  const router = useRouter()
  
  useEffect(() => {  
    let mounted = true;

    (async () => {
      setLoading(true);
      try {
        const [d, configResult] = await Promise.all([
          apiCall.listTable(0, INITIAL_PAGE_SIZE),
          apiCall.config ? apiCall.config().catch(() => []) : Promise.resolve([]),
        ]);
        passLoggedResponse(d, router);
        const responseData = (d as { data?: TableData }).data;
        if (!responseData || !Array.isArray(responseData.body) || !Array.isArray(responseData.config?.head)) {
          throw new Error("Invalid table response");
        }
        const tableData = {
          ...responseData,
          body: mergeUniqueRootRows(responseData.body, responseData.config),
        };
        if (!mounted) return;
        setConfig(Array.isArray(configResult) ? configResult as EditorConfigItem[] : []);
        setData(tableData);
        setHasMore(responseData.body.length === INITIAL_PAGE_SIZE);
        windowStartRef.current = 0;
        nextOffsetRef.current = responseData.body.length;
        const selectedRowIndex = selectedRowIndexRef.current;
        const selectedRow = tableData.body[selectedRowIndex];
        setEditedRow(selectedRow
          ? { rowIndex: selectedRowIndex, row: selectedRow }
          : { rowIndex: -1, row: [] }
        );
        if (!selectedRow) {
          selectedRowIndexRef.current = -1;
          setSelectedRowIndex(-1);
          tableRef.current?.clearVisualSelection();
        } else {
          setSelectedRowIndex(selectedRowIndex);
        }
        setDataVersion((version) => version + 1);
      } catch {
        if (!mounted) return;
        setData(EMPTY_DATA);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [apiCall, router, refreshVersion]);

  const loadNext = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const requestOffset = nextOffsetRef.current;
      const response = await apiCall.listTable(requestOffset, PAGE_SIZE);
      passLoggedResponse(response, router);
      const responseData = (response as { data?: TableData }).data;
      if (!responseData || !Array.isArray(responseData.body) || !Array.isArray(responseData.config?.head)) {
        throw new Error("Invalid table response");
      }
      const nextPage = responseData;
      nextOffsetRef.current = requestOffset + responseData.body.length;
      const visibleRows = mergeUniqueRootRows([...data.body, ...nextPage.body], data.config);

      setData((current) => ({ ...current, body: visibleRows }));
      setHasMore(nextPage.body.length === PAGE_SIZE);
    } catch (error) {
      // Let the table reset its threshold latch after a transient failure.
      throw error;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [apiCall, data.body, data.config, hasMore, router]);

  const loadPrevious = useCallback(async () => {
    if (loadingMoreRef.current || windowStartRef.current <= 0) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const previousOffset = Math.max(0, windowStartRef.current - PAGE_SIZE);
      tableRef.current?.preserveScrollOnPrepend();
      const response = await apiCall.listTable(previousOffset, windowStartRef.current - previousOffset);
      passLoggedResponse(response, router);
      const responseData = (response as { data?: TableData }).data;
      if (!responseData || !Array.isArray(responseData.body) || !Array.isArray(responseData.config?.head)) {
        throw new Error("Invalid table response");
      }
      const previousPage = responseData;
      // Move the window upward and discard the rows at its bottom.
      const previousRows = mergeUniqueRootRows(previousPage.body, data.config);
      const visibleRows = mergeUniqueRootRows([...previousRows, ...data.body], data.config);
      setData((current) => ({ ...current, body: visibleRows }));
      const nextWindowStart = previousOffset;
      windowStartRef.current = nextWindowStart;
      setHasMore(true);

    } catch (error) {
      // Let the table reset its threshold latch after a transient failure.
      throw error;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [apiCall, data.body, data.config, router]);

  const trimTop = useCallback(() => {
    if (data.body.length <= MAX_ROOT_ROWS) return;
    tableRef.current?.preserveScrollOnRemoveTop();
    const removedCount = Math.min(PAGE_SIZE, data.body.length - MAX_ROOT_ROWS);
    setData((current) => ({ ...current, body: current.body.slice(removedCount) }));
    windowStartRef.current += removedCount;
    const currentSelectedIndex = selectedRowIndexRef.current;
    if (currentSelectedIndex >= 0 && currentSelectedIndex < removedCount) {
      selectedRowIndexRef.current = -1;
      setSelectedRowIndex(-1);
      setEditedRow({ rowIndex: -1, row: [] });
      tableRef.current?.clearVisualSelection();
    } else if (currentSelectedIndex >= removedCount) {
      const nextSelectedIndex = currentSelectedIndex - removedCount;
      selectedRowIndexRef.current = nextSelectedIndex;
      setSelectedRowIndex(nextSelectedIndex);
      const nextSelectedRow = data.body[nextSelectedIndex + removedCount];
      if (nextSelectedRow) setEditedRow({ rowIndex: nextSelectedIndex, row: nextSelectedRow });
    }
  }, [data.body]);

  const trimBottom = useCallback(() => {
    if (data.body.length <= MAX_ROOT_ROWS) return;
    setData((current) => ({ ...current, body: current.body.slice(0, current.body.length - PAGE_SIZE) }));
    nextOffsetRef.current = Math.max(windowStartRef.current, nextOffsetRef.current - PAGE_SIZE);
    const firstRemovedIndex = data.body.length - PAGE_SIZE;
    if (selectedRowIndexRef.current >= firstRemovedIndex) {
      selectedRowIndexRef.current = -1;
      setSelectedRowIndex(-1);
      setEditedRow({ rowIndex: -1, row: [] });
      tableRef.current?.clearVisualSelection();
    }
  }, [data.body]);

  const handleSelect = useCallback((update:EditedRow) => {
    selectedRowIndexRef.current = update.rowIndex;
    setSelectedRowIndex(update.rowIndex);
    setEditedRow(update);
  },[]);

  const handleAdd = useCallback(() => {
    selectedRowIndexRef.current = -1;
    setSelectedRowIndex(-1);
    tableRef.current?.clearVisualSelection();
  }, []);

  const handleRestore = useCallback((rowIndex: number) => {
    tableRef.current?.selectRow(rowIndex);
  }, []);

  const handleSave = useCallback(async (data: UpdateRowData) => {
    const text = `Are you sure to ${config[0]?.title || "Save Entry"}?`;
    try {
      const result = await apiCall[config[0]?.handle || 'post'](data) as LoggedResponse;
      if (!result.status) {
        alert(text+' is Failed!');
        handleRestore(editedRow.rowIndex);
        return false;
      }
      alert(text+' is Success!');
      setRefreshVersion((version) => version + 1);
      return true;
    } catch {
      alert(text+'is Failed!');
      handleRestore(editedRow.rowIndex);
      return false;
    }
  }, [apiCall, editedRow.rowIndex, handleRestore,config]);
  
  const handleDelete = useCallback(async (row: EditedRow) => {
    const id = getKeys(data.config, { level: 0, index: 0 }, row.row).find(({ key }) => key === "id")?.value;
    if (id === undefined) {
      alert("ID not found for deletion");
      return {status: false, message: "ID not found"};
    }
    const handle = config[0]?.handle;
    if (!handle || !(handle in apiCall)) {
      alert("Delete handle not found");
      return {status: false, message: "Delete handle not found"};
    }
    const result = await apiCall[handle](id) as LoggedResponse;
    return result;
  }, [apiCall, config, data.config]);

  if (loading) {
    return <div className="p-3">Loading...</div>;
  }

  return (
    <div className='position-relative' style={{ paddingLeft: "3rem", paddingRight: "7rem", }}>
      <NavigationButtons tableRef={tableRef} disabled={isChanged} />
      {/* <pre>{JSON.stringify(savedData.master)}</pre>
      <pre>{JSON.stringify(savedData.detail)}</pre> */}
      <Editor
        title={title}
        key={`${editedRow.rowIndex}-${dataVersion}`}
        data={data}
        row={editedRow}
        config={config}
        onChanged={setChanged}
        onSave={handleSave}
        onDelete={handleDelete}
        onAdd={handleAdd}
        onRestore={handleRestore}
      />
      <Table
        key={dataVersion}
        data={data}
        tableAt={{ level: 0, index: 0 }}
        handleSelect={handleSelect}
        initialSelectedRowIndex={selectedRowIndex}
        onReachStart={loadPrevious}
        onReachEnd={loadNext}
        onTrimTop={trimTop}
        onTrimBottom={trimBottom}
        ref={tableRef}
        readonly
      />
      {loadingMore && <div className="small text-secondary p-2" aria-live="polite">Loading...</div>}
    </div>
  );
}
